import React, { useCallback, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Input, Radio, DatePicker, message, Modal } from 'antd';
import dayjs, { Dayjs } from 'dayjs';
import LookupInput from '../../components/LookupInput';
import { m301Api } from '../../services/m301Api';
import { useModulePermission } from '../../hooks/usePermission';
import PrintControlPanel, { PrintMode } from './PrintControlPanel';
import './M301Form.css';

// ---- Criteria state (mirrors FoxPro PR_START_* / PR_LAST_* variables) ----
interface Criteria {
  warehouseFrom: string;
  warehouseTo: string;
  productFrom: string;
  productTo: string;
  batchFrom: string;
  batchTo: string;
  dateFrom: Dayjs | null;
  dateTo: Dayjs | null;
  includeZeroStock: boolean; // true = OptZERO=1 (含零庫存); false = OptZERO=2 (排除，預設)
}

const initialCriteria = (): Criteria => ({
  warehouseFrom: '',
  warehouseTo: '',
  productFrom: '',
  productTo: '',
  batchFrom: '',
  batchTo: '',
  dateFrom: null,
  dateTo: dayjs(),
  includeZeroStock: false,
});

// ---- BR-02: auto-fill LAST_xx when START_xx loses focus ----
// FoxPro: PR_LAST_01 = SUBSTR(TRIM(PR_START_01)+'zzzzz', 1, 5)
const fillLastFromStart = (start: string, width: number): string => {
  const trimmed = (start || '').trim();
  const padded = trimmed + 'zzzzzzzzzzzz';
  return padded.substring(0, width);
};

const M301Form: React.FC = () => {
  const navigate = useNavigate();
  const permissions = useModulePermission('pwm301f1');

  const [criteria, setCriteria] = useState<Criteria>(initialCriteria());
  const [printMode, setPrintMode] = useState<PrintMode>(1); // 1=預覽 2=列印
  const [startPage, setStartPage] = useState(1);
  const [endPage, setEndPage] = useState(99);
  const [statusMsg, setStatusMsg] = useState('');
  const [busy, setBusy] = useState(false);

  const setField = useCallback(<K extends keyof Criteria>(key: K, value: Criteria[K]) => {
    setCriteria((prev) => ({ ...prev, [key]: value }));
  }, []);

  // BR-02 Valid events: auto-fill LAST when START blurs and LAST is empty
  const handleStart01Blur = useCallback(() => {
    setCriteria((prev) => {
      if (prev.warehouseTo.trim() !== '') return prev;
      return { ...prev, warehouseTo: fillLastFromStart(prev.warehouseFrom, 5) };
    });
  }, []);

  const handleStart02Blur = useCallback(() => {
    setCriteria((prev) => {
      if (prev.productTo.trim() !== '') return prev;
      return { ...prev, productTo: fillLastFromStart(prev.productFrom, 12) };
    });
  }, []);

  const handleStart03Blur = useCallback(() => {
    setCriteria((prev) => {
      if (prev.batchTo.trim() !== '') return prev;
      return { ...prev, batchTo: fillLastFromStart(prev.batchFrom, 12) };
    });
  }, []);

  // Command3.Click — confirm → call backend → open/download PDF
  const handlePrint = useCallback(() => {
    if (!permissions.canPrint) {
      message.warning('您沒有列印權限');
      return;
    }
    Modal.confirm({
      title: '列印確認',
      content: '確認所選範圍,開始列印!',
      okText: '確認',
      cancelText: '取消',
      onOk: async () => {
        setBusy(true);
        setStatusMsg('開始列印時間: ' + new Date().toLocaleTimeString() + '\n資料整理中,請稍後......');
        try {
          const blob = await m301Api.print({
            warehouseFrom: criteria.warehouseFrom,
            warehouseTo: criteria.warehouseTo,
            productFrom: criteria.productFrom,
            productTo: criteria.productTo,
            batchFrom: criteria.batchFrom,
            batchTo: criteria.batchTo,
            dateFrom: criteria.dateFrom ? criteria.dateFrom.format('YYYY-MM-DD') : '',
            dateTo: criteria.dateTo ? criteria.dateTo.format('YYYY-MM-DD') : '',
            includeZeroStock: criteria.includeZeroStock,
          });

          if (!blob || blob.size < 500) {
            setStatusMsg((m) => m + '\n查無此資料!\n結束列印時間: ' + new Date().toLocaleTimeString());
            message.warning('查無此資料');
            return;
          }

          const url = URL.createObjectURL(blob);
          if (printMode === 1) {
            // 預覽：open in new tab
            window.open(url, '_blank');
          } else {
            // 列印：open iframe + trigger print dialog
            const iframe = document.createElement('iframe');
            iframe.style.display = 'none';
            iframe.src = url;
            document.body.appendChild(iframe);
            iframe.onload = () => {
              try {
                iframe.contentWindow?.print();
              } catch (err) {
                // Fallback: open in new tab so user can Ctrl+P
                window.open(url, '_blank');
              }
            };
          }
          setStatusMsg((m) => m + '\n' + new Date().toLocaleTimeString() + ' 列印結束!');
          message.success('PDF 產生成功');
        } catch (e: unknown) {
          const err = e as { response?: { data?: { error?: string } }; message?: string };
          const msg = err.response?.data?.error || err.message || '列印失敗';
          setStatusMsg((m) => m + '\n列印失敗: ' + msg);
          message.error(msg);
        } finally {
          setBusy(false);
        }
      },
    });
  }, [criteria, permissions.canPrint, printMode]);

  // Command1.Click — FoxPro SYS(1037) 印表機設定對話框
  // Browser equivalent: no native printer settings dialog, show a hint.
  const handlePrinterSetup = useCallback(() => {
    Modal.info({
      title: '印表機設定',
      content:
        '瀏覽器安全性限制無法直接開啟系統印表機設定對話框。請使用「預覽」模式，在 PDF 預覽視窗內選擇印表機與頁面設定；或於作業系統的「裝置和印表機」修改預設印表機。',
    });
  }, []);

  const handleExit = useCallback(() => {
    navigate('/erp');
  }, [navigate]);

  return (
    <div className="m301-form">
      <div className="page-wrapper">
        <div className="page-title-bar">
          <div className="page-title-left">
            <nav className="breadcrumb-inline">
              <a
                href="#/erp"
                onClick={(e) => {
                  e.preventDefault();
                  navigate('/erp');
                }}
              >
                首頁
              </a>
              <span>/</span>
              <a
                href="#/erp"
                onClick={(e) => {
                  e.preventDefault();
                  navigate('/erp');
                }}
              >
                庫存管理
              </a>
              <span>/</span>
              <span className="current">總庫存量表</span>
            </nav>
            <span className="title-separator">—</span>
            <h1>總庫存量表</h1>
            <span className="badge">pwm301f1</span>
          </div>
        </div>

        <div className="criteria-panel">
          <div className="criteria-row">
            <label className="criteria-label">庫位代號:[從]</label>
            <LookupInput
              lookupType="warehouse"
              value={criteria.warehouseFrom}
              onChange={(code) => setField('warehouseFrom', code)}
              onCodeConfirm={handleStart01Blur}
              style={{ width: 160 }}
              maxLength={5}
            />
            <label className="criteria-label">[至]</label>
            <LookupInput
              lookupType="warehouse"
              value={criteria.warehouseTo}
              onChange={(code) => setField('warehouseTo', code)}
              onCodeConfirm={() => {}}
              style={{ width: 160 }}
              maxLength={5}
            />
          </div>

          <div className="criteria-row">
            <label className="criteria-label">產品代號:[從]</label>
            <LookupInput
              lookupType="product"
              value={criteria.productFrom}
              onChange={(code) => setField('productFrom', code)}
              onCodeConfirm={handleStart02Blur}
              style={{ width: 160 }}
              maxLength={12}
            />
            <label className="criteria-label">[至]</label>
            <LookupInput
              lookupType="product"
              value={criteria.productTo}
              onChange={(code) => setField('productTo', code)}
              onCodeConfirm={() => {}}
              style={{ width: 160 }}
              maxLength={12}
            />
          </div>

          <div className="criteria-row">
            <label className="criteria-label">批&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;號:[從]</label>
            <Input
              value={criteria.batchFrom}
              onChange={(e) => setField('batchFrom', e.target.value)}
              onBlur={handleStart03Blur}
              style={{ width: 160 }}
              maxLength={12}
            />
            <label className="criteria-label">[至]</label>
            <Input
              value={criteria.batchTo}
              onChange={(e) => setField('batchTo', e.target.value)}
              style={{ width: 160 }}
              maxLength={12}
            />
          </div>

          <div className="criteria-row">
            <label className="criteria-label">入庫日期:[從]</label>
            <DatePicker
              value={criteria.dateFrom}
              onChange={(v) => setField('dateFrom', v)}
              style={{ width: 160 }}
              format="YYYY-MM-DD"
              placeholder="YYYY-MM-DD"
            />
            <label className="criteria-label">[至]</label>
            <DatePicker
              value={criteria.dateTo}
              onChange={(v) => setField('dateTo', v)}
              style={{ width: 160 }}
              format="YYYY-MM-DD"
              placeholder="YYYY-MM-DD"
            />
          </div>

          <div className="criteria-row">
            <label className="criteria-label">庫存量為零</label>
            <Radio.Group
              value={criteria.includeZeroStock ? 1 : 2}
              onChange={(e) => setField('includeZeroStock', e.target.value === 1)}
            >
              <Radio value={1}>包含零庫存</Radio>
              <Radio value={2}>排除零庫存</Radio>
            </Radio.Group>
          </div>
        </div>

        {/* FoxPro Pwtcmdp1 container — 456x205 PrintControlPanel */}
        <PrintControlPanel
          mode={printMode}
          onModeChange={setPrintMode}
          startPage={startPage}
          endPage={endPage}
          onStartPageChange={setStartPage}
          onEndPageChange={setEndPage}
          message={statusMsg}
          busy={busy}
          onPrint={handlePrint}
          onPrinterSetup={handlePrinterSetup}
          onExit={handleExit}
        />
      </div>
    </div>
  );
};

export default M301Form;
